import { useState } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: "bot",
      text: "Hi! 👋 Welcome to Why Free Solar. I'm here to help Maryland homeowners go solar. How can I assist you today?",
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [userInfo, setUserInfo] = useState({ name: "", email: "" });
  const [showInfoForm, setShowInfoForm] = useState(true);
  const [isSending, setIsSending] = useState(false);

  const handleInfoSubmit = (e) => {
    e.preventDefault();
    if (!userInfo.name || !userInfo.email) {
      toast.error("Please enter your name and email");
      return;
    }
    setShowInfoForm(false);
    setMessages((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        type: "bot",
        text: `Thanks ${userInfo.name}! What would you like to know about going solar in Maryland?`,
      },
    ]);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const userMessage = inputValue.trim();
    setInputValue("");

    setMessages((prev) => [
      ...prev,
      { id: prev.length + 1, type: "user", text: userMessage },
    ]);

    setIsSending(true);

    try {
      await axios.post(`${API}/chat`, {
        name: userInfo.name,
        email: userInfo.email,
        message: userMessage,
      });
    } catch (error) {
      console.error("Chat save error:", error);
    }

    setTimeout(() => {
      const responses = [
        "Great question! Maryland has excellent solar potential, and our systems typically pay for themselves within 5-7 years. Would you like a free quote?",
        "Yes! With $0 down financing, you start saving from day one. BGE, Pepco, and SMECO customers see the biggest savings.",
        "Our installations come with a 25-year warranty covering panels, inverters, and workmanship. Complete peace of mind!",
        "The installation process typically takes 1-2 days for most Maryland homes. We handle all permits and inspections.",
        "Would you like to schedule a free consultation? Our Maryland solar experts can provide a custom quote for your home.",
      ];
      const randomResponse = responses[Math.floor(Math.random() * responses.length)];

      setMessages((prev) => [
        ...prev,
        { id: prev.length + 1, type: "bot", text: randomResponse },
      ]);
      setIsSending(false);
    }, 1000);
  };

  return (
    <div className="chat-widget" data-testid="chat-widget">
      {isOpen && (
        <div className="absolute bottom-20 right-0 w-[360px] bg-white rounded-2xl shadow-2xl border border-cream-200 overflow-hidden animate-slide-up" data-testid="chat-window">
          {/* Header */}
          <div className="bg-solar-orange p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-bold">Why Free Solar</p>
                <p className="text-white/80 text-sm">Maryland Solar Experts</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white"
              data-testid="chat-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="h-[300px] overflow-y-auto p-4 space-y-4 bg-cream-50">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    message.type === "user"
                      ? "bg-solar-orange text-white rounded-br-sm"
                      : "bg-white text-gray-800 rounded-bl-sm shadow-sm"
                  }`}
                >
                  {message.text}
                </div>
              </div>
            ))}
            {isSending && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 p-3 rounded-2xl rounded-bl-sm shadow-sm">
                  <span className="animate-pulse">Typing...</span>
                </div>
              </div>
            )}
          </div>

          {/* User Info Form or Message Input */}
          {showInfoForm ? (
            <form onSubmit={handleInfoSubmit} className="p-4 bg-white border-t border-cream-200">
              <p className="text-gray-600 text-sm mb-3">
                Please enter your details to start chatting:
              </p>
              <input
                type="text"
                placeholder="Your Name"
                value={userInfo.name}
                onChange={(e) => setUserInfo({ ...userInfo, name: e.target.value })}
                className="w-full input-light h-10 px-3 mb-2 text-sm rounded-lg"
                data-testid="chat-name-input"
              />
              <input
                type="email"
                placeholder="Your Email"
                value={userInfo.email}
                onChange={(e) => setUserInfo({ ...userInfo, email: e.target.value })}
                className="w-full input-light h-10 px-3 mb-3 text-sm rounded-lg"
                data-testid="chat-email-input"
              />
              <Button
                type="submit"
                className="w-full bg-solar-orange text-white hover:bg-solar-orangeDark rounded-full font-bold py-2 text-sm"
                data-testid="chat-start-btn"
              >
                Start Chat
              </Button>
            </form>
          ) : (
            <form onSubmit={sendMessage} className="p-4 bg-white border-t border-cream-200 flex gap-2">
              <input
                type="text"
                placeholder="Type your message..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="flex-1 input-light h-10 px-3 text-sm rounded-full"
                data-testid="chat-message-input"
              />
              <Button
                type="submit"
                disabled={isSending}
                className="bg-solar-orange text-white hover:bg-solar-orangeDark rounded-full p-2 h-10 w-10"
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          )}
        </div>
      )}

      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="chat-bubble"
        data-testid="chat-toggle-btn"
      >
        {isOpen ? (
          <X className="w-7 h-7 text-white" />
        ) : (
          <MessageCircle className="w-7 h-7 text-white" />
        )}
      </button>
    </div>
  );
}
